import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { CheckCircle, Home, FileText } from 'lucide-react';

export default function PedidoConfirmado() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/pedidos/${id}`)
      .then(r => setPedido(r.data))
      .catch(() => navigate('/home'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-brand-bg">
      <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!pedido) return null;

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-start pt-16 px-5 pb-8" data-testid="pedido-confirmado">
      {/* Success animation */}
      <div className="flex flex-col items-center mb-8 animate-slide-in">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-14 h-14 text-green-500" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading text-3xl font-bold text-brand-text text-center">Pedido Recebido!</h1>
        <p className="text-brand-text-muted font-body text-sm mt-1 text-center">
          Seu pedido foi enviado com sucesso
        </p>
      </div>

      {/* Order summary card */}
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Order number header */}
        <div className="bg-brand-primary px-5 py-4 text-white">
          <p className="font-body text-sm opacity-80">Número do pedido</p>
          <p className="font-heading text-2xl font-bold">#{pedido.numero}</p>
        </div>

        {/* Info chips */}
        <div className="px-5 pt-4 pb-2 flex gap-3 flex-wrap">
          {pedido.prazo && (
            <div className="bg-brand-accent-light rounded-xl px-3 py-1.5">
              <p className="text-xs font-body text-brand-text-muted">Prazo</p>
              <p className="text-sm font-dashboard font-bold text-brand-primary">{pedido.prazo}</p>
            </div>
          )}
          {pedido.turno && (
            <div className="bg-brand-accent-light rounded-xl px-3 py-1.5">
              <p className="text-xs font-body text-brand-text-muted">Turno</p>
              <p className="text-sm font-dashboard font-bold text-brand-text">{pedido.turno}</p>
            </div>
          )}
          {pedido.status && (
            <div className="bg-brand-accent-light rounded-xl px-3 py-1.5">
              <p className="text-xs font-body text-brand-text-muted">Status</p>
              <p className="text-sm font-dashboard font-bold text-brand-text">{pedido.status}</p>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="px-5 py-3 border-t border-brand-border">
          <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-2">Itens</p>
          <div className="space-y-1.5">
            {pedido.itens?.map((item, i) => (
              <div key={i} className="flex justify-between items-center">
                <p className="text-sm font-body text-brand-text flex-1 truncate pr-3">{item.descricao}</p>
                <p className="text-sm font-dashboard font-semibold text-brand-text-muted flex-shrink-0">{item.quantidade}x</p>
              </div>
            ))}
          </div>
        </div>

        {/* Observacoes */}
        {pedido.observacoes && (
          <div className="px-5 py-3 border-t border-brand-border">
            <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-1">Observações</p>
            <p className="text-sm font-body text-brand-text">{pedido.observacoes}</p>
          </div>
        )}

        {/* Total */}
        {pedido.total != null && (
          <div className="px-5 py-3 border-t border-brand-border bg-brand-bg/50 flex justify-between items-center">
            <span className="font-dashboard font-bold text-sm text-brand-text">Total</span>
            <span className="font-dashboard font-bold text-lg text-brand-primary">{formatCurrency(pedido.total)}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="w-full max-w-sm mt-6 space-y-3">
        <button
          onClick={() => navigate(`/pedidos/${pedido.id}`)}
          className="w-full py-3.5 rounded-2xl border-2 border-brand-primary text-brand-primary font-dashboard font-semibold text-sm flex items-center justify-center gap-2"
          data-testid="ver-pedido-btn"
        >
          <FileText className="w-4 h-4" />
          Ver Detalhes do Pedido
        </button>
        <button
          onClick={() => navigate('/home')}
          className="w-full py-3.5 rounded-2xl btn-primary font-dashboard font-semibold text-sm flex items-center justify-center gap-2"
          data-testid="ir-inicio-btn"
        >
          <Home className="w-4 h-4" />
          Ir para Início
        </button>
      </div>
    </div>
  );
}
